import { useState } from "react";
import { 
  Activity, 
  ArrowUpRight, 
  ArrowDownRight,
  Terminal,
  Zap,
  Database,
  Wifi,
  Clock,
  ChevronRight,
  Search,
  Command,
  TrendingUp,
  DollarSign,
  Home,
  FileText,
  Users,
  AlertCircle
} from "lucide-react";

// Mockup of infrastructure-style dashboard
export default function DashboardMockup() {
  const [commandOpen, setCommandOpen] = useState(false);

  // Sample data
  const deals = [
    { id: "DL-7842", address: "1247 Oak Valley Dr", city: "Charlotte", status: "lead", price: 145000, arv: 235000, spread: 48000, updated: "2m ago" },
    { id: "DL-7839", address: "892 Pinewood Lane", city: "Raleigh", status: "analyzed", price: 189000, arv: 320000, spread: 66000, updated: "1h ago" },
    { id: "DL-7835", address: "456 Maple Street", city: "Durham", status: "contract", price: 112000, arv: 195000, spread: 48000, updated: "3h ago" },
    { id: "DL-7831", address: "2105 Elm Creek Ct", city: "Charlotte", status: "closing", price: 225000, arv: 385000, spread: 82000, updated: "1d ago" },
    { id: "DL-7828", address: "738 Birch Avenue", city: "Huntersville", status: "closed", price: 135000, arv: 210000, spread: 47000, updated: "3d ago" },
  ];

  const statusColors = {
    lead: "text-white/50 border-white/[0.1] bg-[#0A0A0A]/80 backdrop-blur-sm/50",
    analyzed: "text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/50 bg-blue-500/10",
    contract: "text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/50 bg-amber-500/10",
    closing: "text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] border-purple-500/50 bg-purple-500/10",
    closed: "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/50 bg-emerald-500/10",
  };

  const activityLog = [
    { type: "deal", action: "AI_ANALYSIS_COMPLETE", target: "DL-7839", time: "14:32:01", score: 82 },
    { type: "system", action: "WEBHOOK_RECEIVED", target: "podio_sync", time: "14:31:45", status: "ok" },
    { type: "deal", action: "STATUS_CHANGE", target: "DL-7835", time: "14:28:12", from: "analyzed", to: "contract" },
    { type: "comms", action: "SMS_SENT", target: "+1704****142", time: "14:25:33", status: "delivered" },
    { type: "system", action: "CRON_JOB", target: "drip_processor", time: "14:00:00", status: "ok" },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] text-white font-sans">
      {/* Command Palette Hint */}
      <div 
        onClick={() => setCommandOpen(true)}
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-black border border-white/[0.06] px-3 py-1.5 rounded-sm cursor-pointer hover:border-white/[0.08] transition-all duration-300"
      >
        <Search className="w-3.5 h-3.5 text-white/40" />
        <span className="text-xs text-white/40">Search deals, actions...</span>
        <div className="flex items-center gap-0.5 ml-4">
          <kbd className="px-1.5 py-0.5 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-[2px] text-[10px] font-mono font-bold text-white/50">⌘</kbd>
          <kbd className="px-1.5 py-0.5 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-[2px] text-[10px] font-mono font-bold text-white/50">K</kbd>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex">
        {/* Sidebar - Minimal */}
        <aside className="w-14 border-r border-white/[0.06] bg-black flex flex-col items-center py-4 gap-1">
          <div className="w-8 h-8 bg-gradient-to-br from-[#D4AF37] to-[#B8972E] rounded-sm flex items-center justify-center mb-6">
            <Terminal className="w-4 h-4 text-black" />
          </div>
          <NavIcon icon={Activity} active />
          <NavIcon icon={Home} />
          <NavIcon icon={FileText} />
          <NavIcon icon={Users} />
          <NavIcon icon={Database} />
          <div className="flex-1" />
          <NavIcon icon={Zap} />
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4">
          {/* Top Stats Row */}
          <div className="grid grid-cols-5 gap-3 mb-4">
            <StatCard label="ACTIVE DEALS" value="5" change="+2" positive />
            <StatCard label="PIPELINE VALUE" value="$806K" change="+12%" positive />
            <StatCard label="AVG SPREAD" value="$58.2K" change="-3%" positive={false} />
            <StatCard label="CLOSE RATE" value="68%" change="+5%" positive />
            <StatCard label="AI QUEUE" value="0" status="idle" />
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-3 gap-3">
            {/* Deals Table - Takes 2 columns */}
            <div className="col-span-2 bg-black/50 border border-white/[0.06] rounded-sm">
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono font-bold text-white/40 uppercase tracking-wider">Active Deals</span>
                  <span className="px-1.5 py-0.5 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-[2px] text-[10px] font-mono font-bold text-white/50">5</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-[2px] text-[9px] font-mono font-bold text-white/40">N</kbd>
                  <span className="text-[10px] text-white/30">new</span>
                </div>
              </div>
              
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]/50">
                    <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-left px-3 py-2">ID</th>
                    <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-left px-3 py-2">Property</th>
                    <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-left px-3 py-2">Status</th>
                    <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-right px-3 py-2">Price</th>
                    <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-right px-3 py-2">Spread</th>
                    <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-right px-3 py-2">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal) => (
                    <tr key={deal.id} className="border-b border-white/[0.06]/30 hover:bg-[#0A0A0A]/80 backdrop-blur-sm/30 cursor-pointer group">
                      <td className="px-3 py-2">
                        <span className="font-mono text-xs text-white/40">{deal.id}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col">
                          <span className="text-white/90 text-sm">{deal.address}</span>
                          <span className="text-white/40 text-xs">{deal.city}, NC</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded-[2px] text-[10px] font-mono uppercase tracking-wider border ${statusColors[deal.status]}`}>
                          {deal.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="font-mono font-bold text-white/70">${(deal.price / 1000).toFixed(0)}K</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="font-mono text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">${(deal.spread / 1000).toFixed(0)}K</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="font-mono text-xs text-white/40">{deal.updated}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Activity Log */}
            <div className="bg-black/50 border border-white/[0.06] rounded-sm">
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
                <span className="text-xs font-mono font-bold text-white/40 uppercase tracking-wider">Activity Log</span>
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
              </div>
              
              <div className="divide-y divide-white/[0.06]/50">
                {activityLog.map((log, idx) => (
                  <div key={idx} className="px-3 py-2 hover:bg-[#0A0A0A]/80 backdrop-blur-sm/30 cursor-pointer">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-[10px] text-[#D4AF37] uppercase">{log.action}</span>
                      <span className="font-mono text-[10px] text-white/30">{log.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-white/50">{log.target}</span>
                      {log.score && (
                        <span className="px-1 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded-[2px] text-[9px] font-mono text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]">
                          SCORE:{log.score}
                        </span>
                      )}
                      {log.status === "ok" && (
                        <span className="px-1 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded-[2px] text-[9px] font-mono text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
                          OK
                        </span>
                      )}
                      {log.status === "delivered" && (
                        <span className="px-1 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded-[2px] text-[9px] font-mono text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
                          DELIVERED
                        </span>
                      )}
                      {log.from && (
                        <span className="text-[10px] text-white/40">
                          {log.from} → {log.to}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-4 gap-3 mt-3">
            <QuickAction label="New Deal" shortcut="N" icon={Home} />
            <QuickAction label="AI Analyze" shortcut="A" icon={Zap} />
            <QuickAction label="SMS Messaging" shortcut="S" icon={Activity} />
            <QuickAction label="Export" shortcut="E" icon={FileText} />
          </div>
        </main>
      </div>

      {/* Status Bar */}
      <footer className="fixed bottom-0 left-0 right-0 h-7 bg-black border-t border-white/[0.06] flex items-center justify-between px-4 text-[10px] font-mono">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
            <span className="text-white/40">CONNECTED</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/30">
            <Database className="w-3 h-3" />
            <span>MongoDB</span>
            <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">●</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/30">
            <Zap className="w-3 h-3" />
            <span>AI Engine</span>
            <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">●</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/30">
            <Wifi className="w-3 h-3" />
            <span>Webhooks</span>
            <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">●</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-white/30">Last sync: 14s ago</span>
          <span className="text-white/30">v2.1.0-beta</span>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-white/30" />
            <span className="text-white/50">14:32:15 EST</span>
          </div>
        </div>
      </footer>

      {/* Command Palette Modal */}
      {commandOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center pt-[20vh]" onClick={() => setCommandOpen(false)}>
          <div className="w-[560px] bg-black border border-white/[0.08] rounded-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
              <Search className="w-4 h-4 text-white/40" />
              <input 
                type="text" 
                placeholder="Search deals, run actions..." 
                className="flex-1 bg-transparent text-sm text-white placeholder:text-white/30 outline-none font-mono"
                autoFocus
              />
              <kbd className="px-1.5 py-0.5 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-[2px] text-[10px] font-mono font-bold text-white/40">ESC</kbd>
            </div>
            <div className="p-2">
              <div className="text-[10px] text-white/40 uppercase tracking-wider px-2 py-1">Quick Actions</div>
              <CommandItem icon={Home} label="Create New Deal" shortcut="N" />
              <CommandItem icon={Zap} label="Run AI Analysis" shortcut="A" />
              <CommandItem icon={Search} label="Search All Deals" shortcut="/" />
              <CommandItem icon={Activity} label="View Pipeline" shortcut="P" />
              <div className="text-[10px] text-white/40 uppercase tracking-wider px-2 py-1 mt-2">Recent Deals</div>
              <CommandItem icon={FileText} label="DL-7842 · 1247 Oak Valley Dr" />
              <CommandItem icon={FileText} label="DL-7839 · 892 Pinewood Lane" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper Components
function NavIcon({ icon: Icon, active }) {
  return (
    <button className={`w-9 h-9 rounded-sm flex items-center justify-center transition-all duration-300 ${
      active 
        ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30" 
        : "text-white/40 hover:text-white/70 hover:bg-[#0A0A0A]/80 backdrop-blur-sm"
    }`}>
      <Icon className="w-4 h-4" />
    </button>
  );
}

function StatCard({ label, value, change, positive, status }) {
  return (
    <div className="bg-black/50 border border-white/[0.06] rounded-sm p-3">
      <div className="text-[10px] text-white/40 uppercase tracking-wider font-mono mb-1">{label}</div>
      <div className="flex items-end justify-between">
        <span className="text-xl font-mono font-medium text-white">{value}</span>
        {change && (
          <span className={`flex items-center gap-0.5 text-xs font-mono ${positive ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" : "text-[#FF3B30]"}`}>
            {positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {change}
          </span>
        )}
        {status === "idle" && (
          <span className="px-1.5 py-0.5 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-[2px] text-[9px] font-mono font-bold text-white/50 uppercase">Idle</span>
        )}
      </div>
    </div>
  );
}

function QuickAction({ label, shortcut, icon: Icon }) {
  return (
    <button className="bg-black/50 border border-white/[0.06] rounded-sm p-3 flex items-center justify-between hover:border-white/[0.08] hover:bg-[#0A0A0A]/80 backdrop-blur-sm/50 transition-all duration-300 group">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-white/40 group-hover:text-[#D4AF37]" />
        <span className="text-sm text-white/70">{label}</span>
      </div>
      <kbd className="px-1.5 py-0.5 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-[2px] text-[10px] font-mono font-bold text-white/40">{shortcut}</kbd>
    </button>
  );
}

function CommandItem({ icon: Icon, label, shortcut }) {
  return (
    <div className="flex items-center justify-between px-2 py-2 rounded-sm hover:bg-[#0A0A0A]/80 backdrop-blur-sm cursor-pointer group">
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-white/40 group-hover:text-white/70" />
        <span className="text-sm text-white/70">{label}</span>
      </div>
      {shortcut && (
        <kbd className="px-1.5 py-0.5 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-[2px] text-[10px] font-mono font-bold text-white/40">{shortcut}</kbd>
      )}
    </div>
  );
}
